import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { User, Clock, DollarSign, AlertTriangle, CreditCard, MapPin, Briefcase, Check } from "lucide-react";

const PayrollEntryDetails = ({ entry, isOpen, onClose, formatCurrency }) => {
  const { t } = useTranslation();
  if (!entry) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded-lg">
              <User className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <span className="text-xl font-semibold">{t("payrollUi.entry.title")}</span>
              <p className="text-sm text-muted-foreground font-normal mt-1">
                {entry.employee_full_name} • {entry.employee_registration_number}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {t("payrollUi.entry.empInfo")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { icon: User, label: t("payrollUi.entry.fullName"), value: entry.employee_full_name },
                  { icon: CreditCard, label: t("payrollUi.entry.matricule"), value: entry.employee_registration_number },
                  { icon: MapPin, label: t("payrollUi.entry.department"), value: entry.employee_department || "N/A" },
                  { icon: Briefcase, label: t("payrollUi.entry.position"), value: entry.employee_job_title || "N/A" },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-center gap-3">
                    <div className="p-2 bg-muted rounded-lg">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium">{value}</p>
                      <p className="text-sm text-muted-foreground">{label}</p>
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-muted rounded-lg">
                    <Check className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <Badge variant={entry.is_approved ? "success" : "secondary"}>
                      {entry.is_approved ? t("payrollUi.entry.approved") : t("payrollUi.entry.pending")}
                    </Badge>
                    <p className="text-sm text-muted-foreground">{t("payrollUi.entry.status")}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {t("payrollUi.entry.hoursWorked")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{entry.total_worked_hours || 0}h</div>
                  <p className="text-sm text-muted-foreground">{t("payrollUi.entry.normalHours")}</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">{entry.overtime_hours || 0}h</div>
                  <p className="text-sm text-muted-foreground">{t("payrollUi.entry.overtimeHours")}</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">{entry.night_hours || 0}h</div>
                  <p className="text-sm text-muted-foreground">{t("payrollUi.entry.nightHours")}</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {(entry.total_worked_hours || 0) + (entry.overtime_hours || 0) + (entry.night_hours || 0)}h
                  </div>
                  <p className="text-sm text-muted-foreground">{t("payrollUi.entry.totalHours")}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                {t("payrollUi.entry.salaryDetail")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm">{t("payrollUi.entry.basePay")}</span>
                  <span className="font-medium">{formatCurrency(entry.base_pay)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm">{t("payrollUi.entry.overtimePay")}</span>
                  <span className="font-medium text-orange-600">{formatCurrency(entry.overtime_pay)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm">{t("payrollUi.entry.nightPay")}</span>
                  <span className="font-medium text-purple-600">{formatCurrency(entry.night_differential)}</span>
                </div>
                {entry.bonuses && entry.bonuses > 0 && (
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm">{t("payrollUi.entry.bonuses")}</span>
                    <span className="font-medium text-green-600">{formatCurrency(entry.bonuses)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-3 bg-muted/50 px-4 rounded-lg font-semibold">
                  <span>{t("payrollUi.entry.grossTotal")}</span>
                  <span className="text-lg text-foreground">{formatCurrency(entry.gross_pay)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-red-400">{t("payrollUi.entry.deductions")}</span>
                  <span className="font-medium text-red-600">-{formatCurrency(entry.total_deductions)}</span>
                </div>
                <div className="flex justify-between items-center py-3 bg-green-950/20 px-4 rounded-lg font-bold">
                  <span>{t("payrollUi.entry.netPay")}</span>
                  <span className="text-xl text-green-400">{formatCurrency(entry.net_pay)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {(entry.notes || entry.calculation_date) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  {t("payrollUi.entry.additionalInfo")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {entry.calculation_date && (
                    <p className="text-sm">
                      <span className="font-medium">{t("payrollUi.entry.calcDate")}</span>{" "}
                      {new Date(entry.calculation_date).toLocaleString()}
                    </p>
                  )}
                  {entry.notes && (
                    <p className="text-sm">
                      <span className="font-medium">{t("payrollUi.entry.notes")}</span> {entry.notes}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PayrollEntryDetails;
